import { Box, Tooltip, Typography } from "@mui/joy";
import { Sigma } from "lucide-react";

import { useShowFormulaTooltips } from "../../../../hooks";
import type { MaterialFormulaFlag } from "../../types";
import { FormulaTooltipContent } from "./FormulaTooltipContent";
import { formulaTooltipMaxWidth } from "./materialRowLayout";

const METRIC_LABEL_HEIGHT = 18;

const useFormulaLinkSx = {
    border: "none",
    background: "none",
    cursor: "pointer",
    color: "primary.600",
    fontWeight: 600,
    p: 0,
    lineHeight: 1.2,
    height: METRIC_LABEL_HEIGHT,
    fontFamily: 'var(--std-font-family, "Outfit", sans-serif)',
    fontSize: "0.75rem",
    whiteSpace: "nowrap" as const,
    "&:hover": { textDecoration: "underline" },
};

type FormulaQuantityIndicatorProps = {
    formula?: MaterialFormulaFlag;
    formulaActive?: boolean;
    quantityOverridden?: boolean;
    onResetToFormula?: () => void;
};

export function FormulaQuantityIndicator({
    formula,
    formulaActive,
    quantityOverridden,
    onResetToFormula,
}: FormulaQuantityIndicatorProps) {
    const { enabled: showFormulaTooltips } = useShowFormulaTooltips();

    if (!formula?.hasFormula) return null;

    const accentColor = quantityOverridden
        ? "var(--standard-palette-warning-500)"
        : formulaActive
          ? "var(--standard-palette-success-500)"
          : "var(--standard-palette-neutral-400)";

    const statusLabel = quantityOverridden
        ? "manual qty"
        : formulaActive
          ? "formula active"
          : "Formula inactive";

    const showUseFormula =
        quantityOverridden && formulaActive && onResetToFormula;

    const status = (
        <Box
            component="span"
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.2,
                cursor: showFormulaTooltips ? "default" : undefined,
                minWidth: 0,
            }}
        >
            <Sigma
                size={12}
                strokeWidth={2}
                color={accentColor}
                style={{ flexShrink: 0 }}
            />
            <Typography
                level="body-xs"
                sx={{
                    color: accentColor,
                    lineHeight: 1.2,
                    height: METRIC_LABEL_HEIGHT,
                    mb: -0.5,
                }}
            >
                {statusLabel}
            </Typography>
        </Box>
    );

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: 0,
            }}
        >
            {showFormulaTooltips ? (
                <Tooltip
                    title={<FormulaTooltipContent formula={formula} />}
                    placement="top"
                    variant="outlined"
                    sx={{
                        maxWidth: formulaTooltipMaxWidth,
                        overflow: "hidden",
                        borderRadius: 0,
                    }}
                >
                    {status}
                </Tooltip>
            ) : (
                status
            )}
            {showUseFormula && (
                <Typography
                    component="button"
                    type="button"
                    onClick={onResetToFormula}
                    sx={useFormulaLinkSx}
                >
                    use formula
                </Typography>
            )}
        </Box>
    );
}
